import { t } from '@lingui/core/macro';
import {
  BookingCreateCarRecord,
  BookingRecordCreate,
  PaymentOption,
  StuddedTireSeasonOverrideReason
} from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { BookingUserNotificationDto, NoddiAsyncError, TireHotelPickupDecision } from 'noddi-types';
import { noddiToast } from 'noddi-ui-web';
import { customerRoutes, getSalesItemCarsPayload } from 'noddi-util';
import {
  commonTrackingEvents,
  getStuddedTireSeasonExplanation,
  tracking,
  useBookingActions,
  useBookingAddress,
  useBookingCarsInsuranceInfo,
  useBookingComment,
  useBookingPinLocation,
  useBookingProposalId,
  useBookingSelectedSalesItemsCars,
  useBookingTimeWindow,
  withStuddedTireSeasonOverride
} from 'noddi-web';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerDepartmentSelection } from '../../../../hooks/useCustomerDepartmentSelection';
import { useGetBookingTrackingParams } from '../../../../hooks/useGetBookingTrackingParams';
import { useMobileBookingCustomer } from '../../../../hooks/useMobileBookingCustomer';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';
import { getOwnStorageFacilities, getOwnStorageFacilityPickups } from '../../../../utils/ownStorageFacilityPickup';
import { routePaymentSession } from '../../../../utils/paymentSession';
import {
  getTireHotelLocationSuggestions,
  withTireHotelPickupDecision
} from '../../../../utils/tireHotelLocationSuggestion';
import { useGetConfirmBookingMutationKey } from './useGetConfirmBookingKey';

/** A create-booking attempt that failed; the wheel pickup errors each get their own dialog instead of the generic toast. */
type FailedBookingAttempt = { error: NoddiAsyncError | null; variables: BookingRecordCreate | undefined };

/** Refusals the customer answers in a dialog, so the generic error toast must stay quiet. */
const hasOwnDialog = (error: NoddiAsyncError | null) =>
  getTireHotelLocationSuggestions(error).length > 0 ||
  getOwnStorageFacilities(error).length > 0 ||
  getStuddedTireSeasonExplanation(error) !== null;

export const useCreateBooking = () => {
  const navigate = useNavigate();
  const trackingParams = useGetBookingTrackingParams();
  const confirmBookingMutationKey = useGetConfirmBookingMutationKey();
  const selectedAddress = useBookingAddress();
  const bookingTimeWindow = useBookingTimeWindow();
  const comment = useBookingComment();
  const selectedSalesItemsCars = useBookingSelectedSalesItemsCars();
  const pinLocation = useBookingPinLocation();
  const bookingProposalId = useBookingProposalId();
  const { userGroupId, userId } = useMobileBookingCustomer();
  const { getSelectedDepartmentForGroup } = useCustomerDepartmentSelection();
  const { currentBrand } = useCurrentBrand();
  const carsInsuranceInfo = useBookingCarsInsuranceInfo();
  const { setTireHotelPickupDecision, removeSalesItems } = useBookingActions();
  // Client-observed booking POST latency — backend booking performance as the user feels it.
  const bookingStartedAt = useRef<number | null>(null);

  const { mutateAsync: createPaymentSession, isPending: isCreatingPaymentSession } = noddiAsync.usePost({
    type: URLKeys.postOrderPaymentSession,
    queryConfig: {
      onSuccess: ({ data: sessionData }) => {
        routePaymentSession({
          session: sessionData,
          // oxlint-disable-next-line typescript/no-misused-promises -- routePaymentSession takes navigate typed () => void; react-router's navigate returns a promise that only tracks lazy-route loading
          navigate,
          getEmbeddedPath: (session) =>
            customerRoutes.payForBooking.getPath({
              sessionId: session.sessionId,
              countryCode: session.countryCode
            }),
          navigateOptions: { replace: true }
        });
      },
      onError: (error) => {
        tracking.track(commonTrackingEvents.bookingFailed, {
          stage: 'payment_session',
          errorMessage: error instanceof Error ? error.message : undefined
        });
        noddiToast.error(t`Error creating booking - please contact support if the problem persists`);
      }
    }
  });

  const {
    mutateAsync: createBookingPayBefore,
    isPending: isPayBeforePending,
    error: payBeforeError,
    variables: payBeforeVariables,
    reset: resetPayBefore
  } = noddiAsync.usePost({
    type: URLKeys.postCreateBookingIgnoringImpersonation,
    queryConfig: {
      onMutate: () => {
        bookingStartedAt.current = performance.now();
      },
      onSuccess: async (response) => {
        if (bookingStartedAt.current !== null) {
          tracking.measure('bookingMutationMs', performance.now() - bookingStartedAt.current);
        }
        tracking.track(commonTrackingEvents.bookingCreated, {
          ...trackingParams,
          executingServiceDepartment: response.data?.serviceDepartment
        });
        const order = response.data.order!;
        if (order.amountGross.amount > 0) {
          await createPaymentSession({ orderId: order.id });
        } else {
          navigate(customerRoutes.confirmation.getPath({ slug: response.data.slug }), { replace: true });
        }
      },
      onError: (error) => {
        tracking.track(commonTrackingEvents.bookingFailed, {
          stage: 'create_booking',
          errorMessage: error instanceof Error ? error.message : undefined
        });
        if (hasOwnDialog(error)) {
          return;
        }
        noddiToast.error(t`Error creating booking - please contact support if the problem persists`);
      }
    }
  });

  const { mutate: createBookingPayAfter, isPending: isPayAfterPending } = noddiAsync.usePost({
    type: URLKeys.postCreateBookingIgnoringImpersonation,
    queryConfig: {
      mutationKey: confirmBookingMutationKey,
      onMutate: () => {
        navigate(customerRoutes.newBookingBuilding.getPath());
      },
      // Tracking only — error UI is handled by the booking builder via mutationKey.
      onError: (error) => {
        tracking.track(commonTrackingEvents.bookingFailed, {
          stage: 'create_booking_pay_after',
          errorMessage: error instanceof Error ? error.message : undefined
        });
      }
    }
  });

  const getBookingPayload = ({
    paymentMethod,
    notificationsToOverride = [],
    userGroupPayerId,
    bookingUserGroupId,
    invoiceReference,
    bookedForUserId
  }: {
    paymentMethod?: PaymentOption;
    notificationsToOverride?: BookingUserNotificationDto[];
    /** Set only when invoice is selected and payment options named another recipient. */
    userGroupPayerId?: number | null;
    /** Group the booking is created on. Falls back to the signed-in group. */
    bookingUserGroupId?: number | null;
    /** Reference typed for a non-member invoice. Omitted when checkout did not ask. */
    invoiceReference?: string;
    /** Driver the organization booking is assigned to. Omitted, the booker. */
    bookedForUserId?: number | null;
  }) => ({
    cars: getSalesItemCarsPayload(selectedSalesItemsCars, carsInsuranceInfo) as BookingCreateCarRecord[],
    addressId: selectedAddress!.id,
    // Absent for a booking without a customer visit: the backend dates it to its wheel pickup.
    deliveryWindow: bookingTimeWindow && {
      id: bookingTimeWindow.deliveryWindowId,
      endsAt: bookingTimeWindow.endsAt,
      startsAt: bookingTimeWindow.startsAt
    },
    userId: bookedForUserId ?? userId!,
    userGroupId: bookingUserGroupId ?? userGroupId!,
    customerDepartmentId: getSelectedDepartmentForGroup(bookingUserGroupId ?? userGroupId!)?.id,
    commentsUser: comment ?? '',
    pinLocationLongitude: pinLocation?.lng,
    pinLocationLatitude: pinLocation?.lat,
    bookingProposalId,
    notificationsToOverride,
    payment: paymentMethod ? { paymentMethod, ...(invoiceReference != null ? { invoiceReference } : {}) } : undefined,
    brandDomain: currentBrand,
    ...(userGroupPayerId != null ? { userGroupPayerId } : {})
  });

  const postBooking = async (payload: BookingRecordCreate) => {
    if (payload.payment?.paymentMethod === PaymentOption.PAY_BEFORE) {
      await createBookingPayBefore(payload);
    } else {
      // no await here as we navigate straight to booking builder which handles the loading and tracking
      createBookingPayAfter(payload);
    }
  };

  const createBooking = (args: {
    paymentMethod: PaymentOption;
    notificationsToOverride?: BookingUserNotificationDto[];
    userGroupPayerId?: number | null;
    bookingUserGroupId?: number | null;
    invoiceReference?: string;
    bookedForUserId?: number | null;
  }) => postBooking(getBookingPayload(args));

  /**
   * Dialog props when the attempt asked the customer to confirm or decline a tire hotel location, else null. Answering
   * stores the decision and re-posts the failed payload with it — patched directly, since the store update is not
   * visible to this render's payload builder.
   */
  const getTireHotelSuggestionPrompt = ({ error, variables }: FailedBookingAttempt, onDismiss: () => void) => {
    const suggestions = getTireHotelLocationSuggestions(error);
    if (suggestions.length === 0 || !variables) {
      return null;
    }
    return {
      suggestions,
      onAnswer: (decision: TireHotelPickupDecision) => {
        setTireHotelPickupDecision(decision);
        // oxlint-disable-next-line typescript/no-floating-promises -- failures surface through the mutation state
        postBooking(withTireHotelPickupDecision(variables, decision));
      },
      onDismiss
    };
  };

  const getOwnStorageFacilityPrompt = ({ error }: FailedBookingAttempt, onDismiss: () => void) => {
    const facilities = getOwnStorageFacilities(error);
    if (facilities.length === 0) {
      return null;
    }
    return {
      facilities,
      onChangePickup: () => {
        removeSalesItems(
          getOwnStorageFacilityPickups(selectedSalesItemsCars, error).map(({ licensePlate, salesItemId, type }) => ({
            licensePlate,
            salesItemId,
            type
          }))
        );
        onDismiss();
        navigate(customerRoutes.newBookingTireHotel.getPath());
      },
      onDismiss
    };
  };

  /**
   * Dialog props when the attempt asked the customer to confirm an out-of-season studded-tire date, else null.
   * Confirming re-posts the same payload with the override — the date is never refused.
   */
  const getStuddedTireSeasonPrompt = ({ error, variables }: FailedBookingAttempt, onDismiss: () => void) => {
    if (getStuddedTireSeasonExplanation(error) === null || !variables) {
      return null;
    }
    return {
      onConfirm: () => {
        // oxlint-disable-next-line typescript/no-floating-promises -- failures surface through the mutation state
        postBooking(withStuddedTireSeasonOverride(variables, StuddedTireSeasonOverrideReason.CUSTOMER_REQUEST));
      },
      onDismiss
    };
  };

  const payBeforeAttempt: FailedBookingAttempt = { error: payBeforeError, variables: payBeforeVariables };

  return {
    createBooking,
    getTireHotelSuggestionPrompt,
    getOwnStorageFacilityPrompt,
    getStuddedTireSeasonPrompt,
    /** Pay-before attempts fail in place (summary page); pay-after ones in the booking builder, which reads its own mutation. */
    tireHotelSuggestion: getTireHotelSuggestionPrompt(payBeforeAttempt, resetPayBefore),
    ownStorageFacilityPickup: getOwnStorageFacilityPrompt(payBeforeAttempt, resetPayBefore),
    studdedTireSeason: getStuddedTireSeasonPrompt(payBeforeAttempt, resetPayBefore),
    isPending: isPayBeforePending || isPayAfterPending || isCreatingPaymentSession
  };
};
